import React from 'react';
import {
  Grid,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Tooltip,
  FormHelperText,
  Typography,
  GridProps,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSchemaContext } from './SparkForm';
import { useField } from 'formik';

type DisabledEntity = { value: string; helperComponent?: React.ReactNode };

type SparkFormCheckBoxFieldProps = {
  name: string;
  helpText?: string;
  label?: string | React.ReactNode;
  onChange?: (value: boolean) => void;
  disabledEntities?: DisabledEntity[];
  subHeaderText?: string;
  size?: GridProps['xs'];
};

function SparkFormCheckBoxField({
  name,
  helpText,
  label,
  onChange,
  disabledEntities,
  subHeaderText = '',
  size = 12,
}: SparkFormCheckBoxFieldProps) {
  const [field, meta, helper] = useField<boolean>(name);
  const { validationSchema } = useSchemaContext();

  const isFieldRequired = React.useMemo(() => {
    try {
      const fieldSchema = validationSchema.describe().fields[name];
      //@ts-ignore
      const isRequiredSchema = fieldSchema.tests.find(
        //@ts-ignore
        (test) => test.name === 'required'
      );
      if (isRequiredSchema) {
        return true;
      }

      if ('optional' in fieldSchema) {
        return !fieldSchema.optional;
      }
      return false;
    } catch {
      return false;
    }
  }, [name, validationSchema]);

  const handleChange = (value: boolean) => {
    helper.setValue(value);
    if (onChange) {
      onChange(value);
    }
  };

  const isError = meta.touched && !!meta.error;

  return (
    <Grid item xs={size} display='flex' flexDirection='column' marginLeft={0.5}>
      <FormControlLabel
        control={
          <Checkbox
            checked={field.value}
            onChange={(event) => handleChange(event.target.checked)}
          />
        }
        sx={{ alignItems: 'flex-end' }}
        label={
          <>
            {!!label && (
              <InputLabel
                sx={{
                  display: 'flex',
                  fontSize: 16,
                }}
              >
                <span>{isFieldRequired && '*'}</span>
                {label}
                {helpText && (
                  <Tooltip title={helpText} placement='top' arrow>
                    <InfoOutlinedIcon fontSize='small' />
                  </Tooltip>
                )}
              </InputLabel>
            )}
            {subHeaderText && (
              <Typography color='#A3A3A3' fontSize={12} textAlign='left'>
                {subHeaderText}
              </Typography>
            )}
          </>
        }
      />
      <FormHelperText
        sx={{
          color: (theme) => theme.palette.error.main,
          marginLeft: 1,
          marginBottom: 0.5,
        }}
      >
        {isError ? meta.error : ' '}
      </FormHelperText>
    </Grid>
  );
}

export default SparkFormCheckBoxField;
